import { Brick, registerBrick, Auth, User, CloudObject } from "olympe";
import ReactDOM from "react-dom";
import React, { useState, useEffect } from "react";
import {
    Button,
    CircularProgress,
    Dialog,
    Divider,
    Tooltip,
    Typography
} from "@mui/material";
import IconButton from "@mui/material/IconButton";
import { getIcon } from "../shared/utils/_icons.jsx";
import DialogContent from "@mui/material/DialogContent";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Icon from '@mui/material/Icon';
import {
    createCrisis,
    createBulkCrisis,
    getMaterialsCount,
} from "./RiskCrisis";

export default class UpdateRiskPopup extends Brick {
    /**
     * Executed every time the brick receives a new trigger event.
     * Note that the method will be executed _even if_ an input (data) is undefined.
     *
     * @protected
     * @param {BrickContext} context
     * @param {*} data
     * @param {function()} forwardEvent
     * @param {function(boolean)} setUpdateDone
     */
    onUpdate(
        context,
        [data],
        [forwardEvent, setUpdateDone, setCrisisName, setErrorFlow]
    ) {
        const UpdateRiskPopupId = "updateRiskPopup017e0bbbf3d7db58e51a";
        if (!document.getElementById(UpdateRiskPopupId)) {
            const head = document.body;
            const updateRiskPopup = document.createElement("div");
            updateRiskPopup.id = UpdateRiskPopupId;
            head.appendChild(updateRiskPopup);
        }
        setUpdateDone(false);

        ReactDOM.render(
            <UpdateRiskPopup.Component
                data={data}
                forwardEvent={forwardEvent}
                setUpdateDone={setUpdateDone}
                setCrisisName={setCrisisName}
                setErrorFlow={setErrorFlow}
            />,
            document.getElementById(UpdateRiskPopupId)
        );
    }
}

UpdateRiskPopup.Component = (props) => {
    const [open, setOpen] = useState(true);
    const [loading, setLoading] = useState(false);
    const [risk, setRisk] = useState("");
    const [statusRisk, setStatus] = useState("");
    const [headerRisk, setHeader] = useState("");
    const [actionRisk, setAction] = useState("");
    const [hypercareRisk, setHypercare] = useState("");
    const [severityLevel, setSeverityLevel] = useState("");
    const [isBulk, setIsBulk] = useState(false);
    const [count, setCount] = useState();

    const email = CloudObject.get(Auth.getCurrentUser()).get(User.loginProp);

    useEffect(() => {
        if (
            props.data.moreRecordsAvailable &&
            props.data.checkedItems.length >= 500
        ) {
            try {
                (async () => {
                    setLoading(true);
                    const count = await getMaterialsCount(
                        props.data.stdFilters,
                        props.data.topFilters
                    );
                    setCount(count.counts);
                    setIsBulk(true);
                })();
            } catch (error) {
                props.setErrorFlow(error);
            } finally {
                setLoading(false);
            }
        }
        else {
            setLoading(true)
            setCount(props.data.checkedItems.length)
            setLoading(false)
        }
    }, []);

    const handleClose = () => {
        setOpen(false);
        document.getElementById("updateRiskPopup017e0bbbf3d7db58e51a").remove();
        props.forwardEvent();
    };

    const handleCreateCrisis = async () => {
        const crisisDetails = {
            riskcrisiselement: risk,
            status: statusRisk,
            header: headerRisk,
            action: actionRisk,
            ongoingCrisis: hypercareRisk,
            severityLevel: severityLevel,
            owner: email
        };

        try {
            setLoading(true);
            if (isBulk) {
                console.log("Creating bulk crisis with...");
                const result = await createBulkCrisis(
                    crisisDetails,
                    props.data.stdFilters,
                    props.data.topFilters
                );
                if (result.error) throw new Error("Crisis creation failed");
                props.setUpdateDone(true);
            } else {
                console.log("Creating crisis with...");
                const result = await createCrisis(
                    crisisDetails,
                    props.data.checkedItems
                );
                if (result.error) throw new Error("Crisis creation failed");
                props.setUpdateDone(true);
            }
        } catch (error) {
            console.error("An error occured while creating the crisis");
            props.setUpdateDone(false);
        } finally {
            props.setCrisisName(crisisDetails.header);
            setLoading(false);
        }

        handleClose();
    };

    return (
        <Dialog
            onClose={handleClose}
            open={open}
            aria-labelledby="multiple-edit-popup-title"
            style={{ flexDirection: "row" }}
        >
            <IconButton
                aria-label="close"
                style={{ marginLeft: "auto" }}
                onClick={handleClose}
                size="large"
            >
                {getIcon("Close")}
            </IconButton>
            {loading ? (
                <DialogContent
                    style={{
                        padding: "12px 70px 60px 70px",
                        height: "150px",
                        width: "100px",
                    }}
                >
                    <CircularProgress
                        style={{
                            position: "absolute",
                            top: "48%",
                            left: "48%",
                            width: "20%",
                            height: "auto",
                        }}
                    />
                </DialogContent>
            ) : (
                <DialogContent style={{ padding: "12px 70px 60px 70px" }}>
                    <p
                        id="update-risk-popup-title"
                        className={"update-risk-popup-title"}
                    >
                        Would you like to update the risk?
                    </p>
                    <div>
                        <p className="update-risk-popup-subtitle">
                            You are about to create a crisis for{" "}
                            {isBulk ? <b style={{ color: "red" }}>{count ? count : " "}</b> : <b>{count ? count : " "}</b>} materials based on
                            the filters you applied
                        </p>
                        <Divider
                            variant="middle"
                            style={{ margin: "5px 0px" }}
                        />
                    </div>
                    <div className={"update-risk-popup-element"}>
                        <div className={"update-risk-popup-subtitle"}>
                            Risk/Crisis Element *{" "}
                            <Tooltip
                                title={
                                    <React.Fragment>
                                        <Typography color="inherit">Risk/Crisis Element</Typography><br />
                                        <em>{"Select type of risk or crisis being managed"}</em><br /><br />
                                        <b>{'Available selection:'}</b><br /><br />
                                        <u>{'Geo'}</u>{' - risk/crisis related to certain geographical situation. Examples: war, political unrest, natural catastrophe, border control, Covid19 etc..'}<br /><br />
                                        <u>{'Financial'}</u>{' - vendor bankruptcy'}<br /><br />
                                        <u>{'Sourcing Flexibility'}</u>{' - risk/crisis root caused by single sourcing'}<br /><br />
                                        <u>{'Supplier OTIF'}</u>{' - risk/crisis root caused by vendor performance issues. Example: vendor mismanagement'}<br /><br />
                                        <u>{'Planning'}</u>{' - risk/crisis root caused by vendor not being able to cope with demand. Example: sudden increase of demand or constant under planning'}<br /><br />
                                        <u>{'Other'}</u>{' - use "Other" when a predefine risk/crisis element is not matching with the crisis you are managing'}
                                    </React.Fragment>
                                }
                            >
                                {getIcon("Risk", true, {
                                    style: { color: "red", minWidth: "25px" },
                                })}
                            </Tooltip>
                        </div>
                        <Select
                            label="riskElementSelect"
                            value={risk}
                            onChange={(event) => setRisk(event.target.value)}
                            inputProps={{ "aria-label": "Without label" }}
                            displayEmpty
                            variant="standard"
                        >
                            <MenuItem value={"Geo"}>
                                {getIcon("LocationOnOutlined", true, {
                                    style: { color: "gray", minWidth: "25px" },
                                })}{" "}
                                Geo
                            </MenuItem>
                            <MenuItem value={"Financial"}>
                                {getIcon("AttachMoney", true, {
                                    style: { color: "gray", minWidth: "25px" },
                                })}{" "}
                                Financial
                            </MenuItem>
                            <MenuItem value={"Sourcing Flexibility"}>
                                {getIcon("Search", true, {
                                    style: { color: "gray", minWidth: "25px" },
                                })}{" "}
                                Sourcing Flexibility
                            </MenuItem>
                            <MenuItem value={"Supplier OTIF"}>
                                {getIcon("PersonOutline", true, {
                                    style: { color: "gray", minWidth: "25px" },
                                })}{" "}
                                Supplier OTIF
                            </MenuItem>
                            <MenuItem value={"Planning"}>
                                {getIcon("DescriptionOutlined", true, {
                                    style: { color: "gray", minWidth: "25px" },
                                })}{" "}
                                Planning
                            </MenuItem>
                            <MenuItem value={"Energy"}>
                                {getIcon("BatteryCharging90Icon", true, {
                                    style: { color: "gray", minWidth: "25px" },
                                })}{" "}
                                Energy
                            </MenuItem>
                            <MenuItem value={"Quality Risk"}>
                                <Icon  style={{'font-family':'inherit', width:'25px', 'overflow': 'initial', 'font-size': '1.2rem',}} >Q</Icon> Quality
                            </MenuItem>
                            <MenuItem value={"Other"}>
                                {getIcon("Risk", true, {
                                    style: { color: "gray", minWidth: "25px" },
                                })}{" "}
                                Other
                            </MenuItem>
                        </Select>
                    </div>

                    <div
                        className={"update-risk-popup-subtitle"}
                        style={{ marginBottom: "10px", marginTop: "20px" }}
                    >
                        Risk/Crisis Header *
                        <Tooltip
                            title={
                                <React.Fragment>
                                    <Typography color="inherit">Risk/Crisis Header</Typography><br />
                                    <em>{"Input a header as a title for the risk/ crisis situation being managed – "}<u>{"Please use recommended naming convention for the headers:"}</u></em><br /><br />
                                    <b>{'Short name of the issue-Business-Market-Category-free section'}</b>
                                </React.Fragment>
                            }
                        >
                            {getIcon("Info", true, {
                                style: { minWidth: "25px" },
                            })}
                        </Tooltip>
                    </div>
                    <textarea
                        variant="outlined"
                        id="outlined-multiline-textField-header"
                        placeholder={"Limited to 100 characters"}
                        style={{
                            fontFamily: "Roboto, Helvetica, Arial, sans-serif",
                            color: "rgb(105,105,105)",
                            border: "2px solid rgb(196,196,196)",
                            borderRadius: "4px",
                            fontSize: "12px",
                            resize: "none",
                            width: "100%",
                        }}
                        rows="5"
                        maxlength="100"
                        onChange={(event) => setHeader(event.target.value)}
                    />

                    <div className={"update-risk-popup-hypercare"}>
                        <div className={"update-risk-popup-subtitle"}>
                            Ongoing Crisis? *
                            <Tooltip
                            title={
                                <React.Fragment>
                                    <Typography color="inherit">Ongoing Crisis</Typography><br />
                                    <em>{"Input required to differentiate whether the situation being managed is a risk (preventive) or crisis (reacting to an issue)"}</em><br /><br />
                                </React.Fragment>
                            }
                        >
                            {getIcon("Info", true, {
                                style: { minWidth: "25px" },
                            })}
                        </Tooltip>
                        </div>
                        <Select
                            label="ongoingCrisisSelect"
                            value={hypercareRisk}
                            onChange={(event) =>
                                setHypercare(event.target.value)
                            }
                            inputProps={{ "aria-label": "Without label" }}
                            displayEmpty
                            variant="standard"
                        >
                            <MenuItem value={"Yes"}>Yes</MenuItem>
                            <MenuItem value={"No"}>No</MenuItem>
                        </Select>
                    </div>
                    <div className={"update-risk-popup-hypercare"}>
                        <div className={"update-risk-popup-subtitle"}>
                            Severity Level? *
                            <Tooltip
                                title={
                                    <React.Fragment>
                                        <Typography color="inherit">Severity Level</Typography><br />
                                        <em>{"Select type of severity level for the risk or crisis being managed"}</em><br /><br />
                                        <b>{'Available selection:'}</b><br /><br />
                                        <u style={{color:'red'}}>{'High'}</u>{' - Imminent OOS (within the coming 1-2 months) and low/no availability of supply alternative'}<br /><br />
                                        <u style={{color:'yellow'}}>{'Medium'}</u>{' - Imminent OOS (within the coming 1-2 months) but availability of supply alternative'}<br /><br />
                                        <u style={{color:'lightgreen'}}>{'Low'}</u>{' - In more than 2-month time OOS and availability of supply alternative'}
                                    </React.Fragment>
                                }
                            >
                                {getIcon("Info", true, {
                                    style: { minWidth: "25px" },
                                })}
                            </Tooltip>
                        </div>
                        <Select
                            label="severityLevelSelect"
                            value={severityLevel}
                            onChange={(event) =>
                                setSeverityLevel(event.target.value)
                            }
                            inputProps={{ "aria-label": "Without label" }}
                            displayEmpty
                            variant="standard"
                        >
                            <MenuItem value={"High"}>High</MenuItem>
                            <MenuItem value={"Medium"}>Medium</MenuItem>
                            <MenuItem value={"Low"}>Low</MenuItem>
                        </Select>
                    </div>
                    <div
                        className={"update-risk-popup-subtitle"}
                        style={{ marginBottom: "10px", marginTop: "20px" }}
                    >
                        Action *
                        <Tooltip
                            title={
                                <React.Fragment>
                                    <Typography color="inherit">Action</Typography><br />
                                    <em>{"Detailed input on action being taken"}</em>
                                </React.Fragment>
                            }
                        >
                            {getIcon("Info", true, {
                                style: { minWidth: "25px" },
                            })}
                        </Tooltip>
                    </div>
                    <textarea
                        variant="outlined"
                        id="outlined-multiline-textField-action"
                        placeholder={"Type an action - limited to 1000 characters"}
                        style={{
                            fontFamily: "Roboto, Helvetica, Arial, sans-serif",
                            color: "rgb(105,105,105)",
                            border: "2px solid rgb(196,196,196)",
                            borderRadius: "4px",
                            fontSize: "12px",
                            resize: "none",
                            width: "100%",
                        }}
                        rows="5"
                        maxlength="1000"
                        onChange={(event) => setAction(event.target.value)}
                    />
                    <div className={"update-risk-popup-status"}>
                        <div className={"update-risk-popup-subtitle"}>
                            Action Status *
                            <Tooltip
                                title={
                                    <React.Fragment>
                                        <Typography color="inherit">Action Status</Typography><br />
                                        <em>{"Select action status for the risk or crisis being managed to describe where you are in rectifying the situation"}</em><br /><br />
                                        <b>{'Available selection:'}</b><br /><br />
                                        <u style={{color:'red'}}>{'Mitigation/BCP being identified'}</u>{' - action to be identified and aligned'}<br /><br />
                                        <u style={{color:'yellow'}}>{'Mitigation/BCP identified'}</u>{' - action in progress'}<br /><br />
                                        <u style={{color:'lightgreen'}}>{'Mitigation/BCP ready'}</u>{' - action implemented and issue solved'}
                                    </React.Fragment>
                                }
                            >
                                {getIcon("Info", true, {
                                    style: { minWidth: "25px" },
                                })}
                            </Tooltip>
                        </div>
                        <Select
                            label="statusSelect"
                            value={statusRisk}
                            onChange={(event) => setStatus(event.target.value)}
                            inputProps={{ "aria-label": "Without label" }}
                            displayEmpty
                            variant="standard"
                        >
                            <MenuItem value={"Mitigation/BCP ready"}>
                                <div style={{ display: "flex" }}>
                                    <div
                                        className={"rc-risk-table-risk-bubble"}
                                        style={{
                                            backgroundColor: "#64A80A",
                                            marginTop: "0.4rem",
                                        }}
                                    >
                                        {""}
                                    </div>
                                    <div>Mitigation/BCP Ready</div>
                                </div>
                            </MenuItem>
                            <MenuItem value={"Mitigation/BCP identified"}>
                                <div style={{ display: "flex" }}>
                                    <div
                                        className={"rc-risk-table-risk-bubble"}
                                        style={{
                                            backgroundColor: "#F19100",
                                            marginTop: "0.4rem",
                                        }}
                                    >
                                        {""}
                                    </div>
                                    <div>Mitigation/BCP Identified</div>
                                </div>
                            </MenuItem>
                            <MenuItem value={"Mitigation/BCP Being identified"}>
                                <div style={{ display: "flex" }}>
                                    <div
                                        className={"rc-risk-table-risk-bubble"}
                                        style={{
                                            backgroundColor: "#D00032",
                                            marginTop: "0.4rem",
                                        }}
                                    >
                                        {""}
                                    </div>
                                    <div>Mitigation/BCP Being identified</div>
                                </div>
                            </MenuItem>
                        </Select>
                    </div>
                    <div className={"update-risk-popup-save-button-container"}>
                        <Button
                            className={"update-risk-popup-save-button"}
                            variant={"contained"}
                            disabled={
                                !risk ||
                                !statusRisk ||
                                !headerRisk ||
                                !actionRisk ||
                                !hypercareRisk ||
                                !severityLevel
                            }
                            onClick={handleCreateCrisis}
                        >
                            Yes, proceed to update the information to all
                            selected items
                        </Button>
                    </div>
                </DialogContent>
            )}
        </Dialog>
    );
};

registerBrick("017e0bbbf3d7db58e51a", UpdateRiskPopup);
